import { zodResolver } from "@hookform/resolvers/zod";
import { Anchor, Button, Loader, LoadingOverlay, Stepper, Text } from "@mantine/core";
import { TruckIcon } from "@phosphor-icons/react";
import { createFileRoute, Outlet, useNavigate, useParams } from "@tanstack/react-router";
import parsePhoneNumberFromString from "libphonenumber-js";
import { useEffect, useRef, useState } from "react";
import { FormProvider, SubmitHandler, useForm, useFormContext } from "react-hook-form";
import { toast } from "react-hot-toast";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { EventListener } from "react-lottie";
import { z } from "zod";

import { LErrorIcon, LSuccessIcon } from "@/assets/lottie";
import { Fields } from "@/components/@custom/Fields";
import { Input } from "@/components/@custom/Input";
import { IMPLEMENT_TYPES } from "@/constants";
import { useSession } from "@/hooks/@custom/useSession";
import { useTruck } from "@/hooks/useTruck";
import { GLOBAL_SCHEMA } from "@/lib/zod";
import { SaveEditTruck, SaveNewTruck, useLazyGetIsTruckQuery, useLazyGetTruckQuery, useSaveEditTruckMutation, useSaveNewTruckMutation } from "@/store/apis/nextcargo";
import { useGetDriverQuery } from "@/store/apis/nextcargo/driver";
import { cn } from "@/utils/cn";
import { cva } from "@/utils/cva.config";

type ImplementType = typeof IMPLEMENT_TYPES;

type Schema = z.infer<typeof schema>;
const schema = z.object({
  truck: z.object({ ...GLOBAL_SCHEMA.TRUCK.shape, owner: z.string().optional() })
});

export const Route = createFileRoute("/app/caminhao")({
  component: DriverRegisterTruck
});
export function DriverRegisterTruck() {
  const { driver } = useSession();
  const params = useParams({ from: "/app/caminhao/editar/$id", shouldThrow: false });
  const isEditTruck = !!params?.id;

  const truckId = params?.id;
  const driverId = driver?.id;
  const driverCompanyId = driver?.company.id;
  const driverName = driver?.name;

  const { data: driverData } = useGetDriverQuery({ id: driverId! }, { skip: !driverId });
  const [saveNewTruck] = useSaveNewTruckMutation();
  const [saveEditTruck] = useSaveEditTruckMutation();
  const [getIsTruck] = useLazyGetIsTruckQuery();
  const [getTruck] = useLazyGetTruckQuery();

  const { brands, models, fetchOptions } = useTruck();

  const [activeStep, setActiveStep] = useState(0);
  const [submitStatus, setSubmitStatus] = useState({ success: false, error: false });

  const methods = useForm<Schema>({
    mode: "all",
    resolver: zodResolver(schema),
    defaultValues: async () => {
      const defaultData: Schema = {
        truck: {
          owner: "",
          plate: "",
          renavam: "",
          brand: "",
          model: "",
          color: "",
          implementType: "",
          shaftQuantity: 0,
          grossCapacity: 0,
          netCapacity: 0
        }
      };

      if (!isEditTruck) {
        return defaultData;
      }

      if (!params.id) {
        return defaultData;
      }

      const isParamNumber = /^\d+$/.test(String(params.id));
      if (!isParamNumber) {
        toast.error("ID inválido!");
        return defaultData;
      }

      try {
        const { data: truck, isSuccess } = await getTruck({ id: Number(params.id) }, true);
        if (!isSuccess) return defaultData;
        const { licensePlate, vehicleModel, ...innerTruckRest } = truck;

        return {
          truck: {
            plate: licensePlate,
            brand: String(vehicleModel.truckBrand.id),
            model: String(vehicleModel.id),
            ...innerTruckRest
          }
        };
      } catch (_error) {
        return defaultData;
      }
    }
  });

  const brand = methods.watch("truck.brand");
  const implementType = methods.watch("truck.implementType") as ImplementType[number]["type"];

  const implementTypes = IMPLEMENT_TYPES.map((item) => {
    return { value: item.type, label: item.type };
  });

  const onSubmit: SubmitHandler<Schema> = async (data) => {
    const { truck } = data;
    const { error, isSuccess } = await getIsTruck({ plate: truck.plate });

    const enabledObj = {
      enabled: true,
      status: true
    };

    if (!isSuccess && error) {
      toast.error(
        () => {
          const phone = parsePhoneNumberFromString("5511999999999", { defaultCountry: "BR" });
          console.log(phone);
          const wpUrl = `https://api.whatsapp.com/send?phone=${phone?.number}`;

          return (
            <Text fw="bold" size="lg">
              Não foi possível obter informações da placa informada, entre em contato com o suporte para realizar a verificação.{" "}
              <Anchor href={wpUrl} target="_blank" rel="noopener noreferrer" c="green">
                Entrar em contato
              </Anchor>
            </Text>
          );
        },
        {
          id: "create-truck-error",
          duration: Infinity
        }
      );
      setSubmitStatus((prev) => ({ ...prev, success: true }));
      nextStep();
      enabledObj.enabled = false;
      enabledObj.status = false;
    }

    if (!driverId || !driverName || !driverCompanyId) return toast.error("Erro ao obter o ID do motorista. Por favor, faça login novamente.");

    const { name, company, shippingCompany } = driverData!;
    const body: SaveNewTruck["args"] | SaveEditTruck["args"] = {
      driver: {
        id: driverId!
      },
      company: {
        id: company.id
      },
      shippingCompany: shippingCompany?.id ? { id: shippingCompany.id } : null,
      owner: name,
      color: truck.color,
      licensePlate: truck.plate,
      implementType: truck.implementType,
      grossCapacity: truck.grossCapacity,
      netCapacity: truck.netCapacity,
      shaftQuantity: truck.shaftQuantity,
      renavam: truck.renavam,
      vehicleModel: {
        id: Number(truck.model),
        truckBrand: {
          id: Number(truck.brand)
        }
      },
      ...enabledObj
    };

    if (!isEditTruck) {
      const { error } = await saveNewTruck(body);

      if (error && "status" in error && error.status === 502) {
        setSubmitStatus((prev) => ({ ...prev, error: true }));
        return;
      }
      setSubmitStatus((prev) => ({ ...prev, success: true }));
      nextStep();
      return;
    }

    if (!params.id) return toast.error("ID do caminhão não encontrado.");

    body.id = Number(params.id);
    await saveEditTruck(body as SaveEditTruck["args"]);
    setSubmitStatus((prev) => ({ ...prev, success: true }));
    nextStep();
    return;
  };

  const stepFields = ["truck"] as const;
  const validateCurrentStep = async () => {
    if (activeStep >= stepFields.length) return true;

    const isValid = await methods.trigger(stepFields);
    return isValid;
  };
  const nextStep = async () => {
    const isValid = await validateCurrentStep();

    if (!isValid) {
      toast.error("Pra avançar para o próximo passo, preencha os campos corretamente!");
      return;
    }
    setActiveStep((prev) => (prev < 3 ? prev + 1 : prev));
  };
  const changeActiveStep = (step: number) => {
    if (step < 0 || step > 2) return;
    setActiveStep(step);
  };
  const prevStep = () => setActiveStep((prev) => (prev > 0 ? prev - 1 : prev));

  const calledBrand = useRef(false);
  useEffect(() => {
    if (calledBrand.current) return;

    if (activeStep === 0) {
      calledBrand.current = true;
      fetchOptions("brands");
    }
  }, [activeStep]);

  useEffect(() => {
    if (!brand) return;

    if (!isEditTruck) {
      methods.trigger("truck.model");
      methods.setValue("truck.model", "");
      methods.setFocus("truck.model");
    }

    fetchOptions("models", brand);
  }, [brand]);

  useEffect(() => {
    if (!implementType) return;

    const findInplementType = (type: ImplementType[number]["type"]) => {
      return IMPLEMENT_TYPES.find((item) => item.type === type);
    };

    const found = findInplementType(implementType);
    if (!found) return;
    methods.setValue("truck.shaftQuantity", found.shaftQuantity);
    methods.setValue("truck.grossCapacity", found.grossCapacity);
    methods.setValue("truck.netCapacity", found.netCapacity);
  }, [implementType]);

  /* ############################################################ */

  return (
    <>
      <div className="w-full max-w-md py-8 md:max-w-xl">
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit, console.log)} autoComplete="off" className="relative w-full overflow-hidden rounded-lg bg-white p-4 shadow-xl">
            <LoadingOverlay visible={methods.formState.isLoading} />

            <Stepper active={activeStep} onStepClick={setActiveStep} size="sm" classNames={{ steps: "!hidden md:!flex " }} wrap={false}>
              <Stepper.Step
                label="Primeiro passo"
                description="Inserir dados do caminhão"
                icon={<TruckIcon weight="bold" />}
                color={methods.getFieldState("truck").invalid ? "red" : ""}
                loading={brands.isLoading}
              >
                <Step.One brands={brands} models={models} implementsTypes={implementTypes} isEditTruck={!!isEditTruck} />
              </Stepper.Step>

              <Stepper.Step label="Segundo passo" description="Revisar dados do caminhão" loading={methods.formState.isSubmitting} icon={<TruckIcon weight="bold" />}>
                <Step.Two brands={brands.data} models={models.data} />
              </Stepper.Step>

              <Stepper.Completed>
                <Step.Three success={submitStatus.success} error={submitStatus.error} changeActiveStep={changeActiveStep} />
              </Stepper.Completed>
            </Stepper>

            <div className="my-3 flex justify-between">
              {(activeStep !== 0 && activeStep !== 2) || (submitStatus.error && activeStep !== 0) ? (
                <Button variant="transparent" onClick={prevStep} leftSection={<MdKeyboardArrowLeft />} size="sm">
                  Voltar
                </Button>
              ) : null}

              {activeStep < 1 && (
                <Button onClick={nextStep} rightSection={<MdKeyboardArrowRight />} className={cn([activeStep === 0 && "ml-auto"])} size="sm">
                  Revisar
                </Button>
              )}

              {activeStep === 1 && (
                <Button type="submit" size="sm" tt="uppercase" loading={methods.formState.isSubmitting}>
                  Concluir
                </Button>
              )}
            </div>
          </form>

          <Outlet />
        </FormProvider>
      </div>
    </>
  );
}

const Step = {
  One: StepOne,
  Two: StepTwo,
  Three: StepThree
};

type StepOneProps = {
  models: { data: OptionData[]; isLoading: boolean; isError: boolean };
  brands: { data: OptionData[]; isLoading: boolean; isError: boolean };
  implementsTypes: OptionData[];
  isEditTruck: boolean;
};
function StepOne({ models, brands, implementsTypes, isEditTruck }: StepOneProps) {
  const { control } = useFormContext<Schema>();
  const disabled = { props: { disabled: true, readOnly: true } };

  const _slotProps = isEditTruck ? { grossCapacity: disabled, netCapacity: disabled, shaftQuantity: disabled } : undefined;

  return (
    <Fields.Truck
      models={models}
      brands={brands}
      implementsTypes={implementsTypes}
      // slot={slotProps}
    >
      {isEditTruck && <Input.Text name="truck.owner" control={control} disabled readOnly />}
    </Fields.Truck>
  );
}

type StepTwoProps = {
  models: OptionData[];
  brands: OptionData[];
};
function StepTwo({ models, brands }: StepTwoProps) {
  const { getValues } = useFormContext<Schema>();
  const {
    truck: { model, brand, plate, renavam, color, implementType, shaftQuantity, grossCapacity, netCapacity }
  } = getValues();

  function getLabelByValue<T extends { value: string; label: string }>({ arr = [], value }: { arr: T[]; value: string }) {
    const found = arr.find((item) => item.value === value);
    return found ? found.label : "";
  }

  const modelLabel = getLabelByValue({ arr: models, value: model });
  const brandLabel = getLabelByValue({ arr: brands, value: brand });

  return (
    <div className="space-y-6">
      <h3 className="mb-2 text-lg font-bold">Revisar dados do caminhão</h3>
      <div className="grid grid-cols-1 gap-x-3 gap-y-2 md:grid-cols-2 lg:grid-cols-3">
        <InfoRow label="Placa" value={plate} />
        <InfoRow label="Renavam" value={renavam} />
        <InfoRow label="Marca" value={brandLabel} />
        <InfoRow label="Modelo" value={modelLabel} />
        <InfoRow label="Cor" value={color} />
        <InfoRow label="Tipo Implemento" value={implementType} />
        <InfoRow label="Qtd. Eixos" value={shaftQuantity} />
        <InfoRow label="Capacidade Bruta" value={grossCapacity} />
        <InfoRow label="Capacidade Líquida" value={netCapacity} />
      </div>
    </div>
  );
}

type StepThreeProps = {
  success?: boolean;
  error?: boolean;
  changeActiveStep: (step: number) => void;
};
function StepThree({ success, error, changeActiveStep }: StepThreeProps) {
  const navigate = useNavigate();
  const {
    formState: { isSubmitting, isSubmitSuccessful }
  } = useFormContext<Schema>();

  const handleSuccess = () => {
    navigate({ to: "/app/perfil" });
  };

  const handleError = () => {
    const fiveSeconds = 1000 * 5;

    setTimeout(() => {
      changeActiveStep(0);
    }, fiveSeconds);
  };

  const successListener: EventListener[] = [
    {
      eventName: "complete",
      callback: handleSuccess
    }
  ];

  const errorListener: EventListener[] = [
    {
      eventName: "loopComplete",
      callback: handleError
    }
  ];

  if (isSubmitting) {
    return (
      <div className="flex items-center justify-center py-16 md:py-32">
        <Loader size="xl" />
      </div>
    );
  }

  if (!isSubmitSuccessful || !success || error) return <LErrorIcon options={{ loop: 1 }} eventListeners={errorListener} />;

  return <LSuccessIcon options={{ loop: false }} eventListeners={successListener} />;
}

type InfoRowProps = { label: string; value: string | number };
function InfoRow({ label, value }: InfoRowProps) {
  const row = cva({
    base: "flex flex-col gap-0.5"
  });

  if (typeof value === "string" && value.includes("#")) {
    return (
      <div className={row()}>
        <strong className="text-md font-medium text-gray-500 md:text-xs">{label}:</strong>
        <span className="border-gray size-6 rounded-sm border" style={{ backgroundColor: value }}></span>
      </div>
    );
  }

  return (
    <div className={row()}>
      <span className="text-md font-medium text-gray-500 md:text-xs">{label}:</span>
      <span className="font-semibold break-all text-gray-900">{value || <span className="text-gray-400">-</span>}</span>
    </div>
  );
}
